import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import flights_web_fd_fare_rules_source, {
  useTrackFareRulesSource
} from "../../../../../../../utils/experiments/funnel/flights_web_fd_fare_rules_source";
import flights_web_fd_rename_fare_rules from "../../../../../../../utils/experiments/funnel/flights_web_fd_rename_fare_rules";
import useFareRulesData from "../../../../../../../hooks/useFareRulesData";
import useProfiler from "../../../../../../../hooks/useProfiler";

type FareRulesInfoData = { free: boolean; label: string } | undefined;
export type FareRulesInfo = Record<"changeable" | "cancellable", FareRulesInfoData>;

const useFareRulesInfoController = (flight: UIFlightData) => {
  const i18n = useI18n();
  const profiler = useProfiler();

  const isRenameFareRulesExp = flights_web_fd_rename_fare_rules.getExp(profiler.isMobile()).trackWithDefaultStage();

  const dataSource = useMemo(() => {
    const features = flight?.brandedFareInfo?.features || [];
    const sellableFeatures = flight?.brandedFareInfo?.sellableFeatures || [];
    const allFeatures = [...features, ...sellableFeatures];

    if (!allFeatures.length) return;

    return allFeatures;
  }, [flight?.brandedFareInfo?.features, flight?.brandedFareInfo?.sellableFeatures]);

  const { changeable, cancellable } = useMemo(() => {
    const result: FareRulesInfo = {
      changeable: undefined,
      cancellable: undefined
    };

    if (!dataSource?.length) {
      return result;
    }

    dataSource.forEach((feature) => {
      switch (feature.featureName) {
        case "CHANGEABLE_TICKET":
          if (feature.availability === "INCLUDED") {
            flights_web_fd_fare_rules_source.goals.change_included();
            result.changeable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_no_fee"))
            };
          } else if (feature.availability === "SELLABLE") {
            flights_web_fd_fare_rules_source.goals.change_sellable();
            result.changeable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_fee"))
            };
          }
          break;
        case "REFUNDABLE_TICKET":
          if (feature.availability === "INCLUDED") {
            flights_web_fd_fare_rules_source.goals.cancel_included();
            result.cancellable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_no_fee"))
            };
          } else if (feature.availability === "SELLABLE") {
            flights_web_fd_fare_rules_source.goals.cancel_sellable();
            result.cancellable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_fee"))
            };
          }
          break;
        default:
          break;
      }
    });

    return result;
  }, [dataSource, i18n, isRenameFareRulesExp]);

  const fareRulesData = Array.isArray(flight.fareRules) ? flight.fareRules : flight?.fareRules?.perSegment;

  const standardTicketFeaturesFromFareRules = useFareRulesData(fareRulesData || []);

  const trackFareRulesSource = useTrackFareRulesSource();

  const contentData = useMemo(() => {
    if (trackFareRulesSource()) {
      const result: FareRulesInfo = {
        changeable: undefined,
        cancellable: undefined
      };

      standardTicketFeaturesFromFareRules.forEach((fr) => {
        if (fr.policyType === "change") {
          if (fr.availability === "included") {
            flights_web_fd_fare_rules_source.goals.change_included();
            result.changeable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_no_fee"))
            } as FareRulesInfoData;
          }

          if (fr.availability === "sellable") {
            flights_web_fd_fare_rules_source.goals.change_sellable();
            result.changeable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_fee"))
            } as FareRulesInfoData;
          }
        }

        if (fr.policyType === "cancel") {
          if (fr.availability === "included") {
            flights_web_fd_fare_rules_source.goals.cancel_included();
            result.changeable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_no_fee"))
            } as FareRulesInfoData;
          }

          if (fr.availability === "sellable") {
            flights_web_fd_fare_rules_source.goals.cancel_sellable();
            result.changeable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_fee"))
            } as FareRulesInfoData;
          }
        }
      });

      return result;
    }

    return {
      changeable,
      cancellable
    };
  }, [cancellable, changeable, i18n, isRenameFareRulesExp, standardTicketFeaturesFromFareRules, trackFareRulesSource]);

  const hasFareRules = Boolean(contentData.cancellable || contentData.changeable);

  if (hasFareRules) {
    flights_web_fd_rename_fare_rules.trackStages(profiler, contentData, Boolean(flight.oneWayCombination));
  } else {
    flights_web_fd_fare_rules_source.goals.no_fare_rules();
  }

  return {
    hasFareRules,
    header: i18n.trans(t("flights_fd_fare_rules_header")),
    subheader: isRenameFareRulesExp
      ? i18n.trans(t("flights_fd_fare_rules_subtitle_included_airline"))
      : i18n.trans(t("flights_fd_fare_rules_subhead_policy_info")),
    changeable: contentData.changeable,
    cancellable: contentData.cancellable
  };
};

export default useFareRulesInfoController;
